import moment from 'moment';

String.prototype.toProperCase = function () {
  return this.replace(/\w\S*/g, function(txt){return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();});
};

class Constants {
  constructor() {
    this.statuses = [
      'intake',
      // 'game plan',
      // "restoration",
      "marketing",
      "active",
      "success",
      "sold",
      "terminated",
      "holding",
      "admin review",
      "service",
    ];

    // Notification types for the in-app notification tray. `key` is the value
    // stored on each notification doc (`type`) and in each user's
    // `notificationPrefs` map. Admins toggle these per user on the User
    // Management page; new prefs default on for sales reps only (see
    // defaultNotificationPrefs).
    this.notificationTypes = [
      { key: 'deposit', label: 'Deposit received', description: 'When one of your deals receives a deposit.' },
      { key: 'load_assigned', label: 'Deal assigned to a load', description: 'When one of your deals is added to a shipping load.' },
      { key: 'load_date', label: 'Load ship date', description: 'A day before, and the morning of, the day your deal ships out.' },
      { key: 'title_arrived', label: 'Title arrived', description: 'When a title arrives for one of your deals (sales & consignment reps), or for any car if "All title updates" is on.' },
      { key: 'dmv_packet_ready', label: 'Title ready for processing', description: 'When a car\'s DMV packet is complete and ready to process. Only sent to users with "All title updates" on.' },
      { key: 'dmv_needs_review', label: 'DMV packet needs review', description: 'When a DMV packet has a problem to fix, such as a missing or unreadable page. Only sent to users with "All title updates" on.' },
      { key: 'title_sent', label: 'Title sent', description: 'When a title is sent out for one of your deals (sales & consignment reps), or for any car if "All title updates" is on.' },
      { key: 'so_items_added', label: 'Work added to a service order', description: 'One digest per burst when services, parts, or subcontracted work are added to a service order on your deal.' },
      { key: 'so_status', label: 'Service order quality & payment', description: 'When a service order on your deal moves to quality review or payment.' },
      { key: 'so_part_ordered', label: 'Part ordered', description: 'When a part is ordered for a service order on your deal, with its expected arrival date.' },
      { key: 'so_part_arrived', label: 'Part arrived', description: 'When a part arrives for a service order on your deal.' },
      { key: 'lead_follow_up', label: 'Lead follow-up due', description: 'The morning a follow-up date you set on a lead arrives. Also sends a digest email of that day\'s follow-ups.' },
      { key: 'price_review', label: 'Price reductions ready', description: 'When the weekly price strategy stages reductions for review. Only sent to the reviewers listed in Admin › Price Strategy — unchecking here opts you out even if you are on that list.' },
      { key: 'feed_failure', label: 'Inventory feed failure', description: 'When a nightly inventory feed (cars.com, Classic.com, CarsForSale…) fails or stops uploading. Recipients are set in the feed alert settings.' },
      { key: 'feed_recovered', label: 'Inventory feed recovered', description: 'When a previously failing inventory feed starts uploading again.' },
    ];

    // Types also sent to every user on the title subscriber list
    // (settings/dmv-alerts.notifyUserIds), for every car. The backend sends
    // these to a subscriber unless their pref is explicitly false.
    this.titleSubscriberTypes = ['title_arrived', 'dmv_packet_ready', 'dmv_needs_review', 'title_sent'];

    this.sub_statuses = [
      'inbound',
      'inspection',
      'repair',
      "detail",
      "pictures",
      "video",
      "write-up",
      "sales review",
      "complete",
    ];

    this.order_statuses = [
      'new',
      'estimate',
      // "approval",
      // "parts",
      "ready",
      "on deck",
      "working",
      "quality",
      "payment",
      "complete",
    ];

    // Car statuses that block creating a new service order. A car must NOT be in
    // one of these states to have an SO made for it.
    this.service_ineligible_car_statuses = [
      "sold",
      "terminated",
      "holding",
      "admin review",
    ];

    this.lead_statuses = [
      'pending contact',
      // 'unresponsive',
      'qualifying',
      'closing',
      "dead",
      // "won" is set only by the backend when the car's deal funds
      // (markFundedDealSales). Filters and colors list it; status pickers use
      // manual_lead_statuses, which leaves it out.
      "won",
      "car sold - other buyer",
      // "escalated",
      // "lost",
    ];

    this.manual_lead_statuses = this.lead_statuses.filter((status) => status !== 'won');

    this.service_statuses = [
      'pending',
      // "working",
      // "quality",
      "complete",
    ];

    // Suggested service tags used to group services on the estimate tab and
    // (optionally) on the generated estimate/invoice. The AI prefers these but
    // may coin a new short tag, and users can type a free-form one. Not a hard
    // enum — services may carry any string (or none → grouped under "Other").
    this.service_tags = [
      'Engine',
      'Brakes',
      'Suspension',
      'Steering',
      'Electrical',
      'Cooling',
      'Drivetrain',
      'Transmission',
      'Exhaust',
      'Fuel System',
      'HVAC',
      'Body',
      'Interior',
      'Tires & Wheels',
      'Fluids & Maintenance',
      'Diagnosis',
      'Other',
    ];

    this.part_statuses = [
      'pending',
      'approved',
      'inbound',
      'returning',
      'returned',
      'complete',
      // Terminal: the customer rejected this part. Set by the Decline action
      // (which also keeps the `declined` boolean for accounting). It is never
      // chosen manually from the status dropdown, and is excluded from the
      // parts-pipeline query/tabs the same way 'complete'/'returned' are.
      'declined',
    ];

    // Subcontracted work statuses. Pending = the outside vendor's purchase is
    // still unpaid (isPayable); complete = work done and the purchase is paid.
    // Kept simple on purpose — subcontract items aren't tracked like parts.
    this.subcontract_statuses = [
      'pending',
      'complete',
    ];

    this.part_locations = [
      'in vehicle',
      'office shelf',
      'part room',
      'vehicle shelf',
      ...([1,2,3,4,5,6].map(rack => [1,2,3,4].map(shelf => `R${rack} S${shelf}`))).flat()
    ];

    this.order_types = [
      'detail',
      'pictures',
      "video",
      "repair",
      "purchase",
      "inspection",
      "marketing",
      "misc"
    ]
    .sort();

    this.title_statuses = [
      'receving',
      "processing",
      "complete",
    ];

    this.shipping_statuses = [
      'pending',
      "complete",
    ];

    // Derived shipping-OUT lifecycle for a car, computed at read time from the
    // drop-off load it sits on (see Store.getShippingStatus). Order = pipeline order.
    this.car_shipping_statuses = [
      'not_shipped',
      'on_load',
      'en_route',
      'delivered',
    ];

    this.paint_job_statuses = [
      'pending',
      'prepping',
      'painting',
      'payment',
      'complete',
    ];

    this.trans_types = [
      'automatic',
      'manual',
    ]
    .sort();

    this.approval_statuses = [
      'pending',
      'approved',
    ]

    // Document Library categories — used by the Files page and entity Files tabs.
    this.fileCategories = [
      'title',
      'receipt',
      'bill',
      'memorabilia',
      'form',
      'photo',
      'signed',
      'other',
    ]

    // Bank stipulations — the supporting documents a lender wants before it funds.
    // `key` is the contract shared with the backend; keep it in step with
    // STIP_DOCUMENTS in skyway-functions .../integrations/sales/stips.js.
    this.stipDocuments = [
      { key: 'title_front',     label: 'Title — Front' },
      { key: 'title_back',      label: 'Title — Back' },
      { key: 'consignment_doc', label: 'Signed Consignment Agreement' },
      { key: 'buyer_insurance', label: "Buyer's Insurance" },
      { key: 'sales_doc',       label: 'Signed Bill of Sale' },
      { key: 'signing_log',     label: 'Signing Log' },
    ]

    // Which documents each lender requires, keyed by the `lenders` doc id. Lenders
    // not listed fall back to defaultStipRequirements. The backend owns the same
    // map and is authoritative on a send; this copy drives the picker's defaults.
    this.lenderStipRequirements = {
      jjbest:   ['title_front', 'title_back', 'consignment_doc', 'buyer_insurance'],
      woodside: ['title_front', 'title_back', 'consignment_doc', 'buyer_insurance', 'sales_doc', 'signing_log'],
    }

    this.defaultStipRequirements = ['title_front', 'title_back', 'consignment_doc', 'buyer_insurance']

    this.employees = [
      'Al',
      'Max',
      "Ryan",
      "Mark",
      "Bobby",
      "Roy",
      "Nila",
      "Reanna",
      "Daryll",
      "Kevin",
      // "Karl",
      "Augy",
      "Chris F",
      "Chris H",
      "Chino",
      "Rueben",
      "David",
      "Jayme",
    ]
    .sort();


    this.consignors = [
      "Alan Tanski",
      "Shane Hale",
      "Bobby Chestnut",
      "Ryan Tanski",
      "Mark Tanski",
      "Kurt Johnston",
      "Max Tanski",
      "Trent Ashley"
    ].sort();

    this.sales_team = [
      "Alan Tanski",
      "Bobby Chestnut",
      "Shane Hale",
      "Ryan Tanski",
      "Mark Tanski",
      "Kurt Johnston",
      "Max Tanski",
      "Trent Ashley",
      "Team Tanski",
      "Gavin Troyer"
    ].sort();

    this.timeCodes = [
      {value: "WS|admin labor", label: "Admin"},
      {value: "WS|general break", label: "Break"},
      {value: "WS|cleaning labor", label: "Cleaning"},
      {value: "WS|lunch break", label: "Lunch"},
      {value: "RM|Skyway vehicle repair", label: "Skyway Vehicle"},
      {value: "SCOGS|Non-stock vehicle labor", label: "Non-stock Vehicle"},
      {value: "SCOGS|Non-stock vehicle labor", label: "Clock-out"},
    ];

    this.locations = [
      {value: "FL", label: "Sarasota"},
      {value: "DAL", label: "Dallas"},
      // {value: "DAL", label: "Orange County"},
      // {value: "PHX", label: "Pheonix"},
      // {value: "ATL", label: "Atlanta"},
      // {value: "LAX", label: "Los Angeles"},
      // {value: "CHR", label: "Charlotte"},
      // {value: "HOU", label: "Houston"},
      // {value: "HOU", label: "Denver"},
      // {value: "DTW", label: "Detroit"},
      // {value: "MIA", label: "Miami"},
    ]
    .sort((a, b) => a.value.localeCompare(b.value));
    // this.locations.push({value: "SR", label: "Service"});

    // Physical lots, keyed by the same code as `locations` above. The zip is
    // what shipping quotes price off, so it lives here rather than being parsed
    // back out of the street address.
    // NOTE: ShippingLoadPage/LoadSelector.js also hardcodes these addresses.
    this.skyway_locations = {
      FL:  { label: "Sarasota", address: "10420 Portal Crossing, Sarasota, FL 34211", city: "Sarasota", state: "FL", zip: "34211" },
      DAL: { label: "Dallas",   address: "14805 Venture Dr, Farmers Branch, TX 75234", city: "Farmers Branch", state: "TX", zip: "75234" },
    };

    this.bodyStyles = [
      "Cargo Van",
      "Convertible",
      "Coupe",
      "Hatchback",
      "Minivan",
      "Passenger Van",
      "SUV",
      "Sedan",
      "Truck",
      "Wagon"
    ];

    // Super Dispatch Pricing Insights accepts only these six vehicle types and
    // 400s on anything else, so every bodyStyle above has to fold into one.
    // Keep in sync with the same map in the backend
    // (functions/integrations/shipping/pricingInsights.js).
    this.shipping_vehicle_types = [
      { value: "sedan",          label: "Sedan" },
      { value: "coupe_2_doors",  label: "Coupe (2 door)" },
      { value: "suv",            label: "SUV" },
      { value: "van",            label: "Van" },
      { value: "pickup_2_doors", label: "Pickup (2 door)" },
      { value: "pickup_4_doors", label: "Pickup (4 door)" },
    ];

    this.shipping_vehicle_type_by_body_style = {
      "Coupe": "coupe_2_doors",
      "Convertible": "coupe_2_doors",
      "Sedan": "sedan",
      "Hatchback": "sedan",
      "Wagon": "sedan",
      "SUV": "suv",
      "Cargo Van": "van",
      "Minivan": "van",
      "Passenger Van": "van",
      "Truck": "pickup_2_doors",
    };


    this.cylinderCounts = [3, 4, 5, 6, 8, 10, 12];
    
    this.drivetrains = [
      "All-wheel Drive",
      "Four-wheel Drive",
      "Front-wheel Drive",
      "Rear-wheel Drive",
      "Unknown"
    ];
    
    this.doorCounts = [2, 3, 4, 5, 6, 7, 8, 9, 10];

    // `transmission` on the car is free text, so this is a suggested vocabulary,
    // not a closed list — these are the strings the listing sites index, and a
    // car that needs its own name (a Powerglide, a pre-selector) can still have
    // one typed over it. Mirrored in the backend's sales_review/specs.js.
    this.transmissions = [
      "3-Speed Manual",
      "4-Speed Manual",
      "5-Speed Manual",
      "6-Speed Manual",
      "Manual",
      "2-Speed Automatic",
      "3-Speed Automatic",
      "4-Speed Automatic",
      "5-Speed Automatic",
      "6-Speed Automatic",
      "Automatic",
      "Automated Manual",
      "Continuously Variable"
    ];

    this.userNames = {
      "QaTeMXR0TJcK9L80VTjBo60WpTt2": "Alan Tanski",
      "6u10jX0cGzcEBVq2sUpfd0O3hq42": "Shane Hale",
      "vFtENZjY6dWiMr5m3qCFqAQIy8v2": "Roy Coleman",
      "jaeuSSDch5cuGQztaD7RhZ3acv33": "Bobby Chestnut",
      "W597JhoCdAX26Xxe52BRQcn7hTP2": "Ryan Tanski",
      "FXbkDzwrCVeEda2g2bxPEJLfIFD2": "Augustin Ortiz",
      "HwxpinJD5vgatCaQ3ntyNImPP282": "Mack Besser",
      // "ckFJCBv5bDXXZ4sD6umwmErwCqw1": "Alex Literal",
      "9uhA796j8ShEmIQjFSd7PFxsK4t2": "John Uciechowski",
      "2bnTRpNHBtYYFKE8M9RXFMIEG073": "Noah McKendree",
      // "IqcX1N46dHdgxPDC6vbN4zT9g4q2": "Karl Knueppel",
      "q6OmXvcDOWdBv6lxs41MXpQRgfm1": "Jayme Spencer",
      // "TJxfGmhzuvU9riykprL04GI0V3w2": "Rob Powell",
      "Ppg7VM83fVTugAj1tVifKa8amtF2": "Max Tanski",
      "ESXGyJliPDQkVvSAewGAAWJWteG3": "Ray Jenkins",
      "94xfdgIH44fSJniMf5shQn2aefC2": "Natalie Tanski",
      "5HBSEpaDUOY8GIjbHg7KUInQ59I3": "Walter Lansing",
      "d4eMzjkJqPhUTdPL4zrHFUcvxM63": "Porters",
      "8zj1gUmxaxSUE7suolaqaQUHkmE2": "Sallie Suski",
      "i8vA9okbE8d67MJMPjcY1TI5qaw2": "Mark Tanski",
      "STEVEN_AIENA": "Steven Aiena",
      "ROBERT_PANICO": "Robert Panico",
      
      // "ENRIQUE_ACOSTA": "Enrique Acosta",
    }

    this.mechanics = [
      {name: "Placeholder", rate: 35, cat:"INVR", id: "test", is_active: true},
      // {name: "Alex Literal", rate: 30, cat:"INVR", id: "ckFJCBv5bDXXZ4sD6umwmErwCqw1"},
      {name: "Augustin Ortiz", rate: 45, cat:"INVR", id: "FXbkDzwrCVeEda2g2bxPEJLfIFD2", is_active: true},
      {name: "Mack Besser", rate: 40, cat:"INVR", id: "HwxpinJD5vgatCaQ3ntyNImPP282", is_active: false},
      {name: "Jovani Ortiz", rate: 25, cat:"INVR", id: "MOXC0qR6b8NSZYZ3Lw5oCQtkHj72", is_active: false},
      {name: "John Uciechowski", rate: 50, cat:"INVR", id: "9uhA796j8ShEmIQjFSd7PFxsK4t2", is_active: true},
      {name: "Steven Aiena", rate: 50, cat:"INVR", id: "STEVEN_AIENA", is_active: true},
      {name: "Robert Panico", rate: 50, cat:"INVR", id: "ROBERT_PANICO", is_active: false},
      {name: "Zachary Noriega", rate: 35, cat:"INVR", id: "ZACHARY_NORIEGA", is_active: false},
      {name: "Mayson Roth", rate: 44, cat:"INVR", id: "MASON_ROTH", is_active: true},
      {name: "Eddie Marchese", rate: 35, cat:"INVR", id: "EDDIE_MARCHESE", is_active: true},
      {name: "Eddy Garcia", rate: 20, cat:"INVR", id: "EDDIE_GARCIA", is_active: true},
      // {name: "Enrique Acosta", rate: 40, cat:"INVR", id: "ENRIQUE_ACOSTA"},
      // {name: "Mark Proveaux", rate: 35, cat:"INVR", id: "lwd7yHbNZGSFrB3h4f9504fhXyW2"},
      // {name: "Jayme Spencer", rate: 30, cat:"INVR", id: "q6OmXvcDOWdBv6lxs41MXpQRgfm1"},
      // {name: "Jhonner Caldera", rate: 40, cat:"INVR", id: "3"},
      // {name: "Noah McKendree", rate: 27, cat:"INVR", id: "2bnTRpNHBtYYFKE8M9RXFMIEG073"},
      // {name: "Karl Knueppel", rate: 25, cat:"INVR", id: "IqcX1N46dHdgxPDC6vbN4zT9g4q2"},
      // {name: "Walter Lansing", rate: 42, cat:"INVR", id: "5HBSEpaDUOY8GIjbHg7KUInQ59I3"},
      // {name: "Rob Powell", rate: 35, cat:"INVR", id: "TJxfGmhzuvU9riykprL04GI0V3w2"},
      // {name: "Joe Clouse", rate: 25, cat:"INVR", id: "h7HqTy9tMUNodcaNQ7yZPtHcJzq2"},
    ].sort((a,b) => a.name.split(" ").slice(-1) >= b.name.split(" ").slice(-1) ? 1 : -1);


    this.sales = [
      {name: "Alan Tanski", id: "QaTeMXR0TJcK9L80VTjBo60WpTt2"},
      {name: "Shane Hale", id: "6u10jX0cGzcEBVq2sUpfd0O3hq42"},
      // {name: "Roy Coleman", id: "vFtENZjY6dWiMr5m3qCFqAQIy8v2"},
      // {name: "Bobby Chestnut", id: "jaeuSSDch5cuGQztaD7RhZ3acv33"},
      {name: "Ryan Tanski", id: "W597JhoCdAX26Xxe52BRQcn7hTP2"},
      {name: "Mark Tanski", id: "i8vA9okbE8d67MJMPjcY1TI5qaw2"},
      {name: "Gavin Troyer", id: "uKZzYFWy4LaYYtU8KoO1wAt5kZE3"},
      // {name: "Sallie Grace", id: "8zj1gUmxaxSUE7suolaqaQUHkmE2"},
      // {name: "Ray Jenkins", id: "ESXGyJliPDQkVvSAewGAAWJWteG3"},
    ].sort((a,b) => a.name.split(" ").slice(-1) >= b.name.split(" ").slice(-1) ? 1 : -1);

    this.mechanicNames = [
      "Placeholder",
      // "Alex Literal",
      "Augustin Ortiz",
      // "Enrique Acosta",
      // "Mack Besser",
      // "Jovani Ortiz",
      "John Uciechowski",
      "Steven Aiena",
      "Robert Panico",
      // "Zachary Noriega",
      "Mayson Roth",
      "Eddie Marchese",
      "Eddy Garcia"
      // "Jhonner Caldera",
      // "Noah McKendree",
      // "Karl Knueppel",
      // "Jayme Spencer",
      // "Rob Powell",
      // "Mark Proveaux",
      // "Joe Clouse",
      // "Walter Lansing"
    ];

    const superior_pricing = [
      {
        type: "Quick Wipe",
        description: "Using detail spray wipe off entire car. Usually needed when in a rush for a showing or to knock the dust off a car",
        cost: "$10"
      },
      {
        type: "Full Detail",
        description: "Full detail. Bumper to bumper. Wash exterior, wipe down interior, vacuum, clean all openings (hood, doors, trunk). Clean and wipe down engine and bay. Clean and vacuum trunk. Clean wheels. Shine tires. Clean glass. Done to all new arrivals before photos",
        cost: "$100"
      },
      {
        type: "Wash and Vac for Delivery or Showing",
        description: "Wash or wipe down. Vacuumed interior. Wipe interior. Wipe all openings. Clean glass. Wipe engine and bay",
        cost: "$25"
      },
      {
        type: "Al Special",
        description: "Full detail PLUS other items AI wants addressed. Examples include some under hood paint, paint repair, interior paint, spot polish, etc.",
        cost: "Negotiate"
      },
      {
        type: "1-Step Polish",
        description: "Using DA polish entire car. Mask off plastics",
        cost: "$100"
      },
      {
        type: "2-Step Polish",
        description: "Using a DA cut and polish entire car. Mask off plastics",
        cost: "$200"
      },
      {
        type: "Underside Paint",
        description: "Clean and paint underside of car",
        cost: "$50"
      },
      {
        type: "Trunk Rust Repair",
        description: "Wire wheel rust, fill any holes and paint trunk",
        cost: "$50"
      },
      {
        type: "Other",
        description: "Other items that do not come up on a regular basis",
        cost: "Negotiate"
      }
    ]

    const paint_pricing = [
      {
        type: "Paint Correction",
        description: "Paint correction. Bumper to bumper. Wash exterior, wipe down interior, vacuum, clean all openings (hood, doors, trunk). Clean and wipe down engine and bay. Clean and vacuum trunk. Clean wheels. Shine tires. Clean glass. Done to all new arrivals before photos",
        cost: "$100"
      }
    ];


    this.vendors = [
      {name: "Superior Auto Detailing", type: "detail", pricing: superior_pricing},
      {name: "Duddy", type: "detail", pricing: []},
      {name: "Automotive Architects", type: "detail", pricing: paint_pricing},
    ]

  }

  // Replace the mechanic roster from the Firestore `mechanics` collection while
  // keeping the SAME array references, so the ~20 modules that read
  // constants.mechanics / constants.mechanicNames (some captured at import time)
  // observe the update without a redeploy. Called once at app boot from App.js.
  setMechanics = (list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    const normalized = list
      .map(m => ({
        name: m.name,
        rate: Number(m.rate || 0),
        cat: m.cat || "INVR",
        id: m.id,
        is_active: m.is_active !== false,
        clock_in_enabled: m.clock_in_enabled === true,
        can_edit_lines: m.can_edit_lines === true,
        // 'HH:mm' ET, or '' when this mechanic isn't on the lunch auto clock-out.
        lunch_clock_out: m.lunch_clock_out || "",
        rateSchedule: Array.isArray(m.rateSchedule) ? m.rateSchedule : [],
      }))
      .filter(m => m.id && m.name)
      .sort((a, b) => (a.name.split(" ").slice(-1) >= b.name.split(" ").slice(-1) ? 1 : -1));
    this.mechanics.splice(0, this.mechanics.length, ...normalized);
    this.mechanicNames.splice(
      0,
      this.mechanicNames.length,
      ...normalized.filter(m => m.is_active).map(m => m.name),
    );
  };

  // ── Clocked-labor deconfliction ────────────────────────────────────────
  // Clock-time mechanics launched on this date. A service line completed
  // BEFORE it predates clocked labor, so its flat-rate cost (rate × time) is
  // always real cost. ON/AFTER it, a clock-in mechanic is paid from their
  // timeLogs, so also counting the flat-rate cost on their completed service
  // line would double-count the same work. `status_time` is written as
  // zero-padded 'YYYY/MM/DD' everywhere (Firestore range queries in
  // PerformanceDashboard rely on this), so a plain string compare orders it.
  clockTimeCutoff = '2026/07/07';

  isClockMechanic = (mechanicID) => {
    if (!mechanicID) return false;
    const m = this.mechanics.find((x) => x.id === mechanicID);
    return !!(m && m.clock_in_enabled);
  };

  // A mechanic's pay rate on a given day ('YYYY/MM/DD', default today). Rates
  // are effective-dated: mechanics/{id}.rateSchedule is [{ from, rate }] and the
  // rate for a day is the last entry whose `from` is on or before it. A service
  // line is priced by its status_time day. Mirrors rateOn in the backend's
  // users/rateSchedule.js; change both together.
  rateOn = (mechanicID, day = moment().format('YYYY/MM/DD')) => {
    const m = this.mechanics.find((x) => x.id === mechanicID);
    if (!m) return 0;
    const schedule = [...(m.rateSchedule || [])].sort((a, b) => (a.from < b.from ? -1 : 1));
    if (!schedule.length) return Number(m.rate || 0);
    let rate = Number(schedule[0].rate || 0);
    for (const entry of schedule) {
      if (entry.from <= day) rate = Number(entry.rate || 0);
      else break;
    }
    return rate;
  };

  // How much of a service line's flat-rate cost should count toward an SO's
  // labor total, deconflicted against clocked labor (timeLogs). Returns 0 when
  // the line's cost is already represented by clocked hours.
  countedServiceLaborCost = (service) => {
    if (!service) return 0;
    const cost = Number(service.cost) || 0;
    if (!cost) return 0;
    // Legacy auto-generated "Labor — {Mechanic}" lines: cost comes from timeLogs.
    if (service.source === 'clock') return 0;
    // Completed before clock-time launched (or no completion date) → real cost.
    const t = service.status_time;
    if (!t || t < this.clockTimeCutoff) return cost;
    // On/after cutoff: a clock-in mechanic's labor is their timeLogs, so don't
    // also count the flat-rate cost on their completed line.
    if (this.isClockMechanic(service.mechanicID)) return 0;
    return cost;
  };

  // ── Pay periods ────────────────────────────────────────────────────────
  // Pay is run every two weeks. Periods are anchored to a known START date and
  // every other period is derived from it. `payPeriodAnchor` is a period start
  // (Jun 27, 2026 → Jul 10, 2026 inclusive).
  payPeriodAnchor = '2026-06-27';
  PAY_PERIOD_DAYS = 14;

  // Start (moment) of the 14-day period that contains `date` (default = today).
  getPayPeriodStartFor = (date = moment()) => {
    const anchor = moment(this.payPeriodAnchor).startOf('day');
    const idx = Math.floor(moment(date).startOf('day').diff(anchor, 'days') / this.PAY_PERIOD_DAYS);
    return anchor.clone().add(idx * this.PAY_PERIOD_DAYS, 'days');
  };

  // Full descriptor for the pay period CONTAINING `periodStart` (moment or
  // YYYY-MM-DD). The input is snapped to the 14-day grid, so an off-grid date
  // (stale link, manual URL, old bookmark) still resolves to the correct period.
  getPayPeriod = (periodStart) => {
    const start = this.getPayPeriodStartFor(periodStart);
    const end = start.clone().add(this.PAY_PERIOD_DAYS - 1, 'days').endOf('day');
    return {
      start,
      end,
      startParam: start.format('YYYY-MM-DD'),   // route param
      startYMD: start.format('YYYY/MM/DD'),      // status_time compare (zero-padded)
      endYMD: end.format('YYYY/MM/DD'),
      startMs: start.valueOf(),
      endMs: end.valueOf(),
      label: start.year() === end.year()
        ? `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
        : `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`,
      prevParam: start.clone().subtract(this.PAY_PERIOD_DAYS, 'days').format('YYYY-MM-DD'),
      nextParam: start.clone().add(this.PAY_PERIOD_DAYS, 'days').format('YYYY-MM-DD'),
    };
  };

  // The most recently COMPLETED period — the one before the period containing
  // today. This is the period payroll is actively processing.
  getDefaultPayPeriod = () => {
    return this.getPayPeriod(this.getPayPeriodStartFor().clone().subtract(this.PAY_PERIOD_DAYS, 'days'));
  };

  // ── Vendor pay periods ─────────────────────────────────────────────────
  // Vendors are paid twice a month: the 1st–15th, then the 16th–end of month.
  // This is a different cycle from the mechanic pay periods above. A purchase
  // belongs to the period that contains its `date`, so the Vendor Dashboard and
  // the add-purchase dialog must read the same list of periods.
  VENDOR_PERIOD_MONTHS_BACK = 12;
  VENDOR_PERIOD_MONTHS_FORWARD = 3;

  // All selectable vendor pay periods, oldest first.
  getVendorPayPeriods = () => {
    const periods = [];
    for (let i = -this.VENDOR_PERIOD_MONTHS_BACK; i <= this.VENDOR_PERIOD_MONTHS_FORWARD; i++) {
      const month = moment().add(i, 'months').startOf('month');
      const monthName = month.format('MMM YYYY');
      const lastDay = month.clone().endOf('month').date();

      periods.push({
        value: `${month.format('YYYY-MM')}-01-15`,
        label: `${monthName} 1-15`,
        startDate: month.clone().date(1),
        endDate: month.clone().date(15),
      });
      periods.push({
        value: `${month.format('YYYY-MM')}-16-${lastDay}`,
        label: `${monthName} 16-${lastDay}`,
        startDate: month.clone().date(16),
        endDate: month.clone().endOf('month'),
      });
    }
    return periods;
  };

  // The period containing `date` (default = today), or undefined if it falls
  // outside the selectable window.
  getVendorPayPeriodFor = (date = moment()) => {
    const target = moment(date);
    return this.getVendorPayPeriods().find(
      (p) => target.isSameOrAfter(p.startDate, 'day') && target.isSameOrBefore(p.endDate, 'day')
    );
  };

  // The date to stamp on a purchase filed into `period`: today when today falls
  // inside that period, otherwise the period's last day. This keeps the purchase
  // inside the period the user chose, which is what the dashboard queries on.
  getVendorPayPeriodDate = (period) => {
    const today = moment();
    if (!period) return today.format('YYYY/MM/DD');
    const inPeriod = today.isSameOrAfter(period.startDate, 'day') && today.isSameOrBefore(period.endDate, 'day');
    return (inPeriod ? today : moment(period.endDate)).format('YYYY/MM/DD');
  };

  // A service order can only be created for a car that is NOT in a blocked
  // status (sold / terminated / holding / admin review). Missing/unknown status
  // is treated as eligible to avoid false blocks.
  isCarServiceEligible = (status) => {
    if (!status) return true;
    return !this.service_ineligible_car_statuses.includes(String(status).toLowerCase());
  };

  // Skyway Classics' own "corporate" customer record. It is used for internal /
  // owned-inventory service orders, but must NOT be user-selectable as the
  // customer when creating or editing a service order.
  skyway_classics_customer_id = '9c0d88f5-84f9-454d-833d-a8ced9adad49';

  // "undefined"/"null" ids come from template strings built on a missing id.
  isValidCustomerId = (id) => {
    if (id === undefined || id === null) return false;
    const value = String(id).trim();
    return value !== '' && value !== 'undefined' && value !== 'null';
  };

  // Rejects paths like "customers/undefined" or "customers/".
  isValidDocPath = (path) => {
    if (!path) return false;
    const parts = String(path).split('/');
    return parts.length % 2 === 0 && parts.every(this.isValidCustomerId);
  };

  isSkywayCustomer = (id) => {
    if (!id) return false;
    const value = typeof id === 'object' ? (id.id || id.objectID) : id;
    return String(value || '').trim() === this.skyway_classics_customer_id;
  };

  makeSelects = (type, params = {valueKey: "value", labelKey: "label", includeNone:false}, defaultFilter = () => true) => {
    let selections = this[type].filter(defaultFilter).map(x => ({value: x[params.valueKey || "value"] || x, label: x[params.labelKey || "label"] || x.toProperCase()}));
    if(params.includeNone) selections.unshift({value: "", label: "None"})
    return selections
  }

}

const constants = new Constants();
export default constants;